import React from 'react';
import {
    FaMapMarkerAlt,
    FaClock,
    FaCheck,
    FaTimes,
    FaShieldAlt,
    FaExclamationTriangle,
    FaGlobe,
    FaChild,
    FaBolt
} from 'react-icons/fa';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const getProxiedUrl = (url) => {
    if (!url) return '';
    if (typeof url !== 'string') return '';
    if (url.includes('/api/proxy?url=')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) {
        let base = API_BASE_URL.replace(/\/+$/, '');
        while (base.endsWith('/api')) {
            base = base.slice(0, -4).replace(/\/+$/, '');
        }
        return `${base}/api/proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
};

/** Strip emoji and pictographic characters from CMS or user-authored copy */
function stripEmojis(str) {
    if (str == null) return str;
    if (typeof str !== 'string') return str;
    return str
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/[\uFE0F\u200D]/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export const ExperiencePdfTemplate = ({ experience }) => {
    if (!experience) return null;

    const getCoverImage = () => {
        if (experience.images?.length > 0) {
            const coverImage = experience.images.find(img => img.isCover) || experience.images[0];
            return getProxiedUrl(coverImage.url || coverImage);
        }
        return getProxiedUrl('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop');
    };

    const formatDuration = (duration) => {
        if (!duration) return 'Flexible Duration';
        const parts = [];
        if (duration.days) parts.push(`${duration.days} Day${duration.days > 1 ? 's' : ''}`);
        if (duration.hours) parts.push(`${duration.hours}h`);
        if (duration.minutes) parts.push(`${duration.minutes}m`);
        return parts.join(' ') || 'Flexible Duration';
    };

    // Category specifics processing
    const renderCategoryContent = () => {
        const spec = experience.categorySpecific || {};
        let categoryData = spec;
        if (typeof spec === 'string') {
            try {
                categoryData = JSON.parse(spec);
            } catch (e) {
                categoryData = {};
            }
        }
        if (!categoryData || Object.keys(categoryData).length === 0) return null;

        const formatLabel = (key) => {
            return stripEmojis(
                key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
            );
        };

        const formatValue = (value) => {
            if (Array.isArray(value)) {
                if (value.length === 0) return 'N/A';
                return stripEmojis(value.map((v) => String(v)).join(', '));
            }
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            return stripEmojis(String(value ?? 'N/A'));
        };

        return Object.entries(categoryData)
            .filter(([_, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => ({
                label: formatLabel(key),
                value: formatValue(value)
            }));
    };

    const categorySpecs = renderCategoryContent();

    // Process itinerary
    const processItinerary = () => {
        const itin = experience.itinerary;
        if (!itin) return { hasItinerary: false, isHTML: false, content: null, structured: null };
        if (typeof itin === 'string' && itin.trim().length > 0) {
            return { hasItinerary: true, isHTML: true, content: itin, structured: null };
        }
        if (Array.isArray(itin) && itin.length > 0) {
            const firstItem = itin[0];
            if (typeof firstItem === 'string') {
                return { hasItinerary: true, isHTML: true, content: itin.join(''), structured: null };
            }
            if (typeof firstItem === 'object' && firstItem !== null && (firstItem.hasOwnProperty('step') || firstItem.hasOwnProperty('title'))) {
                return { hasItinerary: true, isHTML: false, content: null, structured: itin };
            }
            if (typeof firstItem === 'object' && firstItem !== null) {
                const keys = Object.keys(firstItem);
                if (keys.length > 0 && keys.every(key => !isNaN(parseInt(key)))) {
                    return { hasItinerary: true, isHTML: true, content: Object.values(firstItem).join(''), structured: null };
                }
            }
        }
        if (typeof itin === 'object' && !Array.isArray(itin) && itin !== null) {
            const keys = Object.keys(itin);
            if (keys.length > 0 && keys.every(key => !isNaN(parseInt(key)))) {
                return { hasItinerary: true, isHTML: true, content: Object.values(itin).join(''), structured: null };
            }
        }
        return { hasItinerary: false, isHTML: false, content: null, structured: null };
    };

    const itinData = processItinerary();

    return (
        <div className="w-[794px] bg-white text-neutral-800 p-0 m-0 shadow-lg select-none" style={{ width: '794px' }}>
            <style>
                {`
                    .pdf-page {
                        width: 794px;
                        height: 1115px;
                        padding: 56px 60px;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        position: relative;
                        background-color: #ffffff;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .pdf-cover-page {
                        width: 794px;
                        height: 1115px;
                        padding: 72px 64px;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        position: relative;
                        background-color: #000000;
                        background-size: cover;
                        background-position: center center;
                        background-repeat: no-repeat;
                        font-family: system-ui, -apple-system, sans-serif;
                        overflow: hidden;
                    }
                    .pdf-title {
                        font-family: Georgia, serif;
                    }
                    .pdf-justify {
                        text-align: justify;
                        text-justify: inter-word;
                        hyphens: auto;
                        -webkit-hyphens: auto;
                    }
                    .quill-content p {
                        margin-bottom: 8px;
                        text-align: justify;
                        text-justify: inter-word;
                        hyphens: auto;
                        -webkit-hyphens: auto;
                    }
                    .quill-content li {
                        text-align: justify;
                        text-justify: inter-word;
                    }
                `}
            </style>

            {/* PAGE 1: COVER PAGE */}
            <div
                className="pdf-cover-page html2pdf__page-break"
                style={{ backgroundImage: `url(${getCoverImage()})` }}
            >
                {/* Dark overlay + gradient for readability */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '794px',
                    height: '1115px',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.45) 50%, rgba(0,0,0,0.25) 100%)',
                    zIndex: 0
                }}></div>

                {/* Header Logo or Brand */}
                {/* <div className="relative z-10 flex justify-between items-center border-b border-white/20 pb-6">
                    <span className="text-white font-bold tracking-wider text-base">AXON TRAVEL GROUP</span>
                    <span className="text-white/60 text-xs tracking-wider uppercase">Curated Experience Brochure</span>
                </div> */}

                {/* Main Cover Title */}
                <div className="relative z-10 space-y-6 my-auto max-w-xl">
                    <p className="text-white/60 font-semibold uppercase tracking-wider text-[10px] flex items-center gap-2">
                        <span className="w-8 h-px bg-white/30"></span>
                        {experience.category ? stripEmojis(experience.category) : 'Adventure'}
                    </p>
                    <h1 className="text-5xl pdf-title font-medium text-white leading-[1.15]">
                        {stripEmojis(experience.title)}
                    </h1>
                    {experience.location && (
                        <p className="text-white/80 font-medium text-sm flex items-center gap-2">
                            <FaMapMarkerAlt className="text-white/60" />
                            {stripEmojis(`${experience.location.city}, ${experience.location.country}`)}
                        </p>
                    )}
                </div>

                {/* Footer Info */}
                {/* <div className="relative z-10 flex justify-between items-end border-t border-white/20 pt-8 text-white/50 text-[10px] tracking-wider uppercase">
                    <div>
                        <p>Prepared for: Travelers</p>
                        <p className="mt-1">Date Generated: {new Date().toLocaleDateString()}</p>
                    </div>
                    <div>
                        <p className="font-semibold text-white">CONFIDENTIAL & PROPRIETARY</p>
                    </div>
                </div> */}
            </div>

            {/* PAGE 2: OVERVIEW & ESSENTIALS */}
            <div className="pdf-page html2pdf__page-break">
                <div className="space-y-8">
                    {/* Header */}
                    {/* <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                        <span className="text-black font-bold tracking-wider text-xs">AXON EXPERIENCE PORTFOLIO</span>
                        <span className="text-gray-400 text-[10px] uppercase font-medium truncate max-w-[300px]">{stripEmojis(experience.title)}</span>
                    </div> */}

                    {/* Overview */}
                    <section className="space-y-3">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Overview</h2>
                            <div className="h-[1px] flex-1 bg-gray-200"></div>
                        </div>
                        <div
                            className="text-gray-600 text-[11px] leading-relaxed prose prose-sm max-w-none quill-content pdf-justify [&_p]:text-inherit [&_li]:text-inherit"
                            dangerouslySetInnerHTML={{ __html: stripEmojis(experience.description || '') }}
                        />
                    </section>

                    {/* Essential Details Grid */}
                    <section className="space-y-3">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Essential Details</h2>
                            <div className="h-[1px] flex-1 bg-gray-200"></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {experience.location && (
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white text-xs shrink-0">
                                        <FaMapMarkerAlt />
                                    </div>
                                    <div>
                                        <p className="text-[7px] font-semibold uppercase text-gray-400">Location</p>
                                        <p className="text-black font-medium text-xs leading-none mt-0.5">{stripEmojis(`${experience.location.city}, ${experience.location.country}`)}</p>
                                    </div>
                                </div>
                            )}
                            <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
                                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white text-xs shrink-0">
                                    <FaClock />
                                </div>
                                <div>
                                    <p className="text-[7px] font-semibold uppercase text-gray-400">Duration</p>
                                    <p className="text-black font-medium text-xs leading-none mt-0.5">{formatDuration(experience.duration)}</p>
                                </div>
                            </div>
                            {experience.difficulty && (
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white text-xs shrink-0">
                                        <FaBolt />
                                    </div>
                                    <div>
                                        <p className="text-[7px] font-semibold uppercase text-gray-400">Difficulty</p>
                                        <p className="text-black font-medium text-xs leading-none mt-0.5 capitalize">{stripEmojis(String(experience.difficulty))}</p>
                                    </div>
                                </div>
                            )}
                            {experience.languages?.length > 0 && (
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white text-xs shrink-0">
                                        <FaGlobe />
                                    </div>
                                    <div>
                                        <p className="text-[7px] font-semibold uppercase text-gray-400">Languages</p>
                                        <p className="text-black font-medium text-xs leading-none mt-0.5">{stripEmojis(experience.languages.join(', '))}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Experience Specifics */}
                    {categorySpecs && categorySpecs.length > 0 && (
                        <section className="space-y-3">
                            <div className="flex items-center gap-3">
                                <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Experience Specifics</h2>
                                <div className="h-[1px] flex-1 bg-gray-200"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {categorySpecs.map((item, idx) => (
                                    <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-100 flex flex-col justify-center">
                                        <p className="text-[7px] font-semibold uppercase text-gray-400 mb-0.5">{item.label}</p>
                                        <p className="text-black font-medium text-xs leading-tight pdf-justify">{item.value}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>

                {/* Footer */}
                {/* <div className="border-t border-gray-100 pt-4 flex justify-between items-center text-[10px] text-gray-400 font-light">
                    <span>AXON TRAVEL GROUP</span>
                    <span>Page 1</span>
                </div> */}
            </div>

            {/* PAGE 3: HIGHLIGHTS & ITINERARY */}
            {(experience.highlights?.length > 0 || itinData.hasItinerary) && (
                <div className="pdf-page html2pdf__page-break">
                    <div className="space-y-8">
                        {/* Header */}
                        {/* <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                            <span className="text-black font-bold tracking-wider text-xs">AXON EXPERIENCE PORTFOLIO</span>
                            <span className="text-gray-400 text-[10px] uppercase font-medium truncate max-w-[300px]">{stripEmojis(experience.title)}</span>
                        </div> */}

                        {/* Highlights */}
                        {experience.highlights?.length > 0 && (
                            <section className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Highlights</h2>
                                    <div className="h-[1px] flex-1 bg-gray-200"></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {experience.highlights.map((highlight, idx) => (
                                        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-900 text-white rounded-xl shadow-sm">
                                            <div className="w-4 h-4 bg-white/10 border border-white/20 rounded flex items-center justify-center text-[8px] text-white shrink-0 mt-0.5">
                                                <FaCheck />
                                            </div>
                                            <p className="text-[11px] leading-relaxed pdf-justify">{stripEmojis(String(highlight))}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Journey Itinerary */}
                        {itinData.hasItinerary && (
                            <section className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Journey Itinerary</h2>
                                    <div className="h-[1px] flex-1 bg-gray-200"></div>
                                </div>

                                {!itinData.isHTML && itinData.structured && (
                                    <div className="space-y-3 relative pl-6 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-0.5 before:bg-black">
                                        {itinData.structured
                                            .sort((a, b) => (a.step || 0) - (b.step || 0))
                                            .map((step, idx) => (
                                                <div key={idx} className="relative space-y-0.5">
                                                    <div className="absolute -left-[22px] top-0.5 w-4 h-4 bg-white border border-black flex items-center justify-center text-black font-bold text-[9px] rounded shadow-sm z-10">
                                                        {step.step || idx + 1}
                                                    </div>
                                                    <div className="p-2.5 bg-gray-50 border border-gray-100 rounded-lg space-y-0.5">
                                                        <div className="flex justify-between items-center gap-2">
                                                            <h4 className="text-xs font-bold text-black">{step.title ? stripEmojis(String(step.title)) : ''}</h4>
                                                            {step.duration && (
                                                                <span className="text-[7px] font-semibold uppercase text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded-full">
                                                                    {stripEmojis(String(step.duration))}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {step.description && (
                                                            <p className="text-gray-600 text-[10px] leading-normal pdf-justify">{stripEmojis(String(step.description))}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}

                                {itinData.isHTML && itinData.content && (
                                    <div className="relative pl-4 border-l border-black ml-1 text-xs">
                                        <div
                                            className="prose prose-sm max-w-none text-[10px] text-gray-600 leading-relaxed quill-content pdf-justify
                                            [&>p]:mb-2 [&>h1]:text-black [&>h1]:font-bold [&>h1]:text-xs [&>h2]:text-black [&>h2]:font-bold [&>h2]:text-[10px]"
                                            dangerouslySetInnerHTML={{ __html: stripEmojis(itinData.content) }}
                                        />
                                    </div>
                                )}
                            </section>
                        )}
                    </div>

                    {/* Footer */}
                    {/* <div className="border-t border-gray-100 pt-4 flex justify-between items-center text-[10px] text-gray-400 font-light">
                        <span>AXON TRAVEL GROUP</span>
                        <span>Page 2</span>
                    </div> */}
                </div>
            )}

            {/* PAGE 4: WHAT'S INCLUDED & VISUAL GALLERY */}
            {(experience.included?.length > 0 || (experience.notIncluded?.length > 0 || experience.excluded?.length > 0) || experience.safetyGuidelines?.length > 0 || experience.images?.length > 1) && (
                <div className="pdf-page html2pdf__page-break">
                    <div className="space-y-8">
                        {/* Header */}
                        {/* <div className="flex justify-between items-center border-b border-gray-100 pb-4">
                            <span className="text-black font-bold tracking-wider text-xs">AXON EXPERIENCE PORTFOLIO</span>
                            <span className="text-gray-400 text-[10px] uppercase font-medium truncate max-w-[300px]">{stripEmojis(experience.title)}</span>
                        </div> */}

                        {/* Included / Not Included Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            {experience.included?.length > 0 && (
                                <div className="p-4 bg-emerald-50/30 rounded-2xl border border-emerald-100/50 space-y-2">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                                        <FaCheck className="text-[9px]" /> What's Included
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {experience.included.map((item, idx) => (
                                            <li key={idx} className="flex gap-1.5 text-gray-700 text-[10px] items-start">
                                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-1.5 shrink-0"></span>
                                                <span className="pdf-justify">{stripEmojis(String(item))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {(experience.notIncluded?.length > 0 || experience.excluded?.length > 0) && (
                                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 space-y-2">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                                        <FaTimes className="text-[9px]" /> Not Included
                                    </h3>
                                    <ul className="space-y-1.5">
                                        {(experience.notIncluded || experience.excluded || []).map((item, idx) => (
                                            <li key={idx} className="flex gap-1.5 text-gray-500 text-[10px] items-start">
                                                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mt-1.5 shrink-0"></span>
                                                <span className="pdf-justify">{stripEmojis(String(item))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Safety Guidelines */}
                        {experience.safetyGuidelines?.length > 0 && (
                            <section className="space-y-2">
                                <div className="flex items-center gap-3">
                                    <h2 className="text-xs pdf-title font-bold uppercase tracking-wider text-black">Safety Guidelines</h2>
                                    <div className="h-[1px] flex-1 bg-gray-200"></div>
                                </div>
                                <div className="p-3 bg-yellow-50/30 rounded-xl border border-yellow-200 space-y-1">
                                    <ul className="space-y-1">
                                        {experience.safetyGuidelines.map((guideline, idx) => (
                                            <li key={idx} className="flex gap-1.5 text-gray-700 text-[10px] items-start">
                                                <FaExclamationTriangle className="text-yellow-600 text-[9px] shrink-0 mt-1" />
                                                <span className="pdf-justify">{stripEmojis(String(guideline))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        )}

                        {/* Visual Narrative Gallery */}
                        {experience.images?.length > 0 && (
                            <section className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <h2 className="text-lg pdf-title font-bold uppercase tracking-wider text-black">Visual Gallery</h2>
                                    <div className="h-[1px] flex-1 bg-gray-200"></div>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    {experience.images.map((image, idx) => (
                                        <div key={idx} className="aspect-[4/3] rounded-lg overflow-hidden border border-gray-150 bg-gray-50">
                                            <img
                                                src={getProxiedUrl(image.url || image)}
                                                crossOrigin="anonymous"
                                                alt={`Moment ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                    </div>
                </div>
            )}


        </div>
    );
};
